/**
 * ExecutionAgent - Tools Section
 * 
 * Standard tools section with preamble and postamble from Keywords AI.
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import type { ToolBase } from '../../../base/toolBase';
import { EXECUTION_AGENT_PROMPT_KEYS } from './variables';

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS PREAMBLE
// ═══════════════════════════════════════════════════════════════════════════

class ExecutionAgentToolsPreambleSection extends PromptSectionBase {
    protected readonly keywordsKey = EXECUTION_AGENT_PROMPT_KEYS.toolsPreamble;
    protected readonly fallbackContent = `You have four essential tools for building slides.

Use them in this pattern:
1. **readSlide** - See the current slide state (structure, shapes, layout)
2. **buildArea** - Add content to a specific area (creates shapes, charts, tables)
3. **Verify via screenshot** - Each buildArea returns a screenshot
4. **rollback** (if needed) - Undo and retry if something looks wrong
5. **submitResults** - Report completion when all areas are built`;
}

const toolsPreambleSection = new ExecutionAgentToolsPreambleSection();

export function getToolsPreamble(): string {
    return toolsPreambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS POSTAMBLE
// ═══════════════════════════════════════════════════════════════════════════

class ExecutionAgentToolsPostambleSection extends PromptSectionBase {
    protected readonly keywordsKey = EXECUTION_AGENT_PROMPT_KEYS.toolsPostamble;
    protected readonly fallbackContent = `**Tool Usage Notes:**
- **buildArea** is your primary tool - use it for each area on each slide
- **readSlide** helps you see what's already there (especially reference slides)
- **rollback** undoes the last buildArea if you need to retry
- **submitResults** is required - call it when ALL slides are complete

**Position values** in buildArea are in POINTS (72 points = 1 inch).
You'll receive area regions in your instructions - use them as guides.`;
}

const toolsPostambleSection = new ExecutionAgentToolsPostambleSection();

export function getToolsPostamble(): string {
    return toolsPostambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS SECTION BUILDER
// ═══════════════════════════════════════════════════════════════════════════

export function buildToolsSection(tools: ToolBase[]): string {
    const parts: string[] = [];
    
    // 1. Preamble
    parts.push(getToolsPreamble());
    parts.push('');
    
    // 2. Individual tool descriptions
    for (const tool of tools) {
        parts.push(tool.systemPromptDescription);
        parts.push('');
    }
    
    // 3. Postamble
    parts.push(getToolsPostamble());
    
    return parts.join('\n');
}
